import { useCallback, useState } from 'react'

/**
 * Putting something on the clipboard, and saying whether it went.
 *
 * The clipboard can refuse — an insecure origin, a browser that has not granted
 * permission, a headless environment — and a copy button that silently did
 * nothing is worse than none, because the next paste is whatever was on the
 * clipboard before. So there are three states and the button says which.
 *
 * A hook rather than a component: a DNS record is copied from beside a line of
 * monospace text and a whole email's markup from beside a textarea, and the two
 * look nothing alike. What they share is this.
 */

export type CopyState = 'idle' | 'copied' | 'refused'

export function useCopy(): { readonly state: CopyState; readonly copy: (value: string) => void } {
  const [state, setState] = useState<CopyState>('idle')

  const copy = useCallback((value: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => setState('copied'))
      .catch(() => setState('refused'))
  }, [])

  return { state, copy }
}
